import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Send } from "lucide-react";

export function CommentsPanel({ comments, currentUser, onSubmit, submitting, locked }) {
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [comments.length]);

  const submit = async () => {
    if (!text.trim() || submitting) return;
    await onSubmit(text.trim());
    setText("");
  };

  return (
    <div className="bg-white border border-black/[0.08] rounded-xl p-5 flex flex-col" data-testid="comments-panel">
      <p className="font-heading font-extrabold text-sm tracking-tight mb-1">Chat with our team</p>
      <p className="text-xs text-neutral-500 mb-4">
        Questions or requests that aren't text edits? Ask here — our team replies right in this chat.
      </p>

      <div ref={scrollRef} className="max-h-72 overflow-y-auto space-y-2.5 mb-3 pr-1" data-testid="chat-messages">
        {comments.length === 0 ? (
          <p className="text-xs text-neutral-400" data-testid="comments-empty-message">
            No messages yet. Start the conversation below.
          </p>
        ) : (
          comments.map((c) => {
            const own = c.author_username === currentUser;
            return (
              <div key={c.id} data-testid={`comment-item-${c.id}`} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 ${own ? "bg-[var(--brand-primary)] text-white rounded-br-sm" : "bg-neutral-100 text-neutral-800 rounded-bl-sm"}`}>
                  {!own && <p className="text-[10px] font-semibold text-neutral-500 mb-0.5">{c.author_name}</p>}
                  <p className="text-xs leading-relaxed whitespace-pre-wrap">{c.text}</p>
                  <p className={`font-mono-accent text-[9px] mt-1 ${own ? "text-white/60" : "text-neutral-400"}`}>
                    {new Date(c.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!locked && (
        <div className="flex items-end gap-2">
          <textarea
            data-testid="comment-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={2}
            placeholder="Type a message..."
            className="flex-1 text-sm border border-black/10 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-[#1058A8]/40 resize-none bg-white"
          />
          <Button
            data-testid="comment-submit-button"
            size="sm"
            disabled={!text.trim() || submitting}
            onClick={submit}
            className="rounded-full h-9 w-9 p-0 bg-[var(--brand-primary)] hover:opacity-90 text-white active:scale-95 transition-colors"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
